import { prescriptionIdentifier } from "@/utils/prescriptionIdentity.js";
import ClinicalNoteProvenance from '@/features/clinical-actions/ClinicalNoteProvenance.jsx';
import { loadClinicalNoteAuthorities } from '@/services/clinicalNoteAuthority';
import ClinicalActionPanel from "@/features/clinical-actions/ClinicalActionPanel.jsx";
import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Dialog from "@/components/ui/Dialog.jsx";
import Divider from "@/components/ui/Divider.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import supabase from "@/services/supabaseClient";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import PatientPasswordLifecyclePanel from "@/pages/profile-settings/patient/PatientPasswordLifecyclePanel.jsx";
import { hasPasswordConfigured } from "@/features/security/accountSecurityState.js";
import {
  hasPatientConsultationParticipation,
  loadPatientAppointmentSnapshot,
} from "@/services/patientAppointmentSnapshots.js";
import {
  fetchLatestPatientVitalSigns,
  formatCanonicalVitals,
} from "@/services/ehrVitalSignsService.js";
import {
  hasVitalRepStructuredClinicalData,
  normalizeVitalRepStructuredClinicalData,
  toVitalRepRecord,
  VITALREP_REPORT_TYPE,
  VITALREP_VITALS_SOURCE,
} from "@/pages/doctors/appointments/lib/vitalRepNote.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";

const PATIENT_EHR_DOCUMENT_BUCKET = "patient-ehr-documents";
const PATIENT_EHR_UPLOAD_SOURCE = "patient_upload";
const PATIENT_EHR_UPLOAD_MIMES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "text/plain",
  "text/rtf",
  "application/rtf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

const PATIENT_EHR_EXTENSION_MIME = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  txt: "text/plain",
  rtf: "text/rtf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

function normalizeId(value) {
  return String(value || "").trim();
}

function safeFilename(value, fallback = "ehr-document") {
  const text = String(value || "").trim();
  const clean = text
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .slice(0, 120)
    .trim();
  return clean || fallback;
}

function fileExtension(filename = "") {
  const match = String(filename || "").match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : "bin";
}

function resolveUploadMime(file, ext) {
  const reported = String(file?.type || "").trim();
  return reported || PATIENT_EHR_EXTENSION_MIME[ext] || "application/octet-stream";
}

function hasDisplayText(value) {
  return String(value || "").trim().length > 0;
}

function isUnavailableText(value) {
  const text = String(value || "").trim().toLowerCase();
  return (
    !text ||
    text === "not available" ||
    text === "not recorded" ||
    text === "not recorded." ||
    text === "n/a" ||
    text === "none"
  );
}

function isRawExportNoise(value) {
  const text = String(value || "").trim();
  if (!text) return true;
  return /^(initial|updated)\s+clinician\s+note\s+\d{4}-\d{2}-\d{2}t/i.test(text);
}

function cleanPatientReadableText(value) {
  return String(value || "")
    .replace(/\\text\{([^}]*)\}/g, "$1")
    .replace(/\\\((.*?)\\\)/g, "$1")
    .replace(/\\ge/g, ">=")
    .replace(/\\le/g, "<=")
    .replace(/\\([{}()[\]<>%])/g, "$1")
    .replace(/\\\\/g, "")
    .replace(/\[[^\]]+\]/g, "")
    .replace(/([.!?])(?=\s*(?:Assessment|Plan|Diagnosis|Prescription|Treatment|Follow-up)\s*:)/gi, "$1 ")
    .replace(/[ \t]*•[ \t]*/g, "\n• ")
    .replace(/(?:^|\n)[ \t]*[-*][ \t]+/g, "\n• ")
    .replace(/(?:^|\n)[ \t]*(\d+)\.[ \t]+/g, "\n$1. ")
    .replace(/\n[ \t]*\n/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/^"+|"+$/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function firstReadableSentence(value) {
  const text = cleanPatientReadableText(value).replace(/\s+/g, " ").trim();
  if (!text || isRawExportNoise(text)) return "";
  const match = text.match(/[^.!?]+[.!?]?/);
  return String(match?.[0] || text).trim();
}

function pickFirstText(...values) {
  for (const value of values) {
    if (hasDisplayText(value) && !isRawExportNoise(value)) return String(value).trim();
  }
  return "";
}

function pickFirstAvailableText(...values) {
  for (const value of values) {
    const text = cleanPatientReadableText(value);
    if (text && !isUnavailableText(text) && !isRawExportNoise(text)) return text;
  }
  return "";
}

function firstAvailableSentence(value) {
  const text = firstReadableSentence(value);
  return text && !isUnavailableText(text) ? text : "";
}

function displayVisitTitle(value, fallback = "Visit") {
  const text = String(value || "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  if (/[A-Z]{2,}/.test(text) || /[()]/.test(text)) return text;

  const minorWords = new Set(["and", "or", "the", "of", "for", "to", "in", "on", "with", "a", "an"]);
  return text
    .split(" ")
    .map((word, index) => {
      if (!word) return word;
      const lower = word.toLowerCase();
      if (index > 0 && minorWords.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function noteSectionValue(value, t) {
  const text = pickFirstAvailableText(value);
  return text || t("settings.subflows.patientEhr.notCaptured", "Not captured");
}

function normalizeVitalDisplayValue(key, value) {
  const text = cleanPatientReadableText(value)
    .replace(/\bkg'?s\b/gi, "kg")
    .replace(/\bkgs\b/gi, "kg")
    .replace(/\s+\/\s*/g, "/")
    .replace(/\s{2,}/g, " ")
    .trim();
  if (!text) return "";
  if (key === "weight") {
    const match = text.match(/^(-?\d+(?:\.\d+)?)\s*(?:kg)?$/i);
    return match ? `${match[1]} kg` : text;
  }
  if (key === "respiratoryRate") {
    const match = text.match(/^(\d+)(?:\s*(?:breaths)?\s*\/?\s*min)?$/i);
    return match ? `${match[1]}/min` : text.replace(/\s*breaths\s*\/\s*min\b/i, "/min");
  }
  return text;
}

function formatDateTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "";
  }
}

function formatDateOnly(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return "";
  }
}

function getAppointmentTimestamp(row = {}) {
  return (
    row?.appointment_date ||
    row?.scheduled_for ||
    row?.starts_at ||
    row?.updated_at ||
    row?.created_at ||
    ""
  );
}

function normalizeRecordStatus(value, fallback = "Not available") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^\w/, (char) => char.toUpperCase());
}

function medicineTitleFromPrescription(row, fallback) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  return (
    pickFirstText(meta?.medicineName, meta?.medicine, meta?.name, meta?.title, row?.external_rx_id) ||
    fallback
  );
}

function buildMyEhrExport(record = EMPTY_RECORD) {
  const latestVitals = formatCanonicalVitals(record.latestVitals).map(([key, value]) => ({
    key,
    value,
  }));
  return {
    exported_at: new Date().toISOString(),
    source: "afrimed_my_ehr_patient_export",
    latest_visit_summary: record.latestVisit
      ? {
          appointment_id: record.latestVisit.id,
          date: record.latestVisit.dateLabel,
          title: record.latestVisit.serviceLabel,
          diagnosis: record.latestVisit.diagnosis,
          notes_preview: record.latestVisit.notesPreview,
        }
      : null,
    latest_vitals: {
      captured_at: record.latestVitals?.captured_at || record.latestVitals?.recorded_at || null,
      source: record.latestVitals?.source || null,
      values: latestVitals,
    },
    visits: (record.visits || []).map((visit) => ({
      appointment_id: visit.id,
      date: visit.dateLabel,
      title: visit.serviceLabel,
      status: visit.statusLabel,
      vitals_source: visit.vitalsSource,
      vitals: Object.fromEntries(visit.vitals || []),
      soap: {
        subjective_reason_for_visit: visit.patientSummary?.reason || "",
        objective_what_was_found: visit.patientSummary?.objective || "",
        assessment_diagnosis: visit.patientSummary?.found || "",
        tests_labs: visit.patientSummary?.testsLabs || "",
        plan_treatment_next_steps: visit.patientSummary?.treatment || "",
        follow_up: visit.patientSummary?.followUp || "",
      },
    })),
    prescriptions: record.prescriptions || [],
    referrals: record.referrals || [],
    notes: record.notes || [],
  };
}

function firstSentence(value) {
  const text = cleanPatientReadableText(value)
    .replace(/visit reason\s*\/\s*context/gi, "")
    .replace(/assessment\s*\/\s*diagnosis summary/gi, "")
    .replace(/clinical notes\s*\/\s*observations/gi, "")
    .replace(/immediate plan\s*\/\s*next actions/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text || isRawExportNoise(text)) return "";
  const match = text.match(/[^.!?]+[.!?]?/);
  return String(match?.[0] || text).trim();
}

function extractStructuredSection(value, headingPattern) {
  const text = String(value || "");
  if (!text.trim()) return "";
  const pattern = new RegExp(
    `${headingPattern}\\s*([\\s\\S]*?)(?=\\n\\s*[A-Z][^\\n]*\\/[^\\n]*\\n|$)`,
    "i",
  );
  const match = text.match(pattern);
  return String(match?.[1] || "")
    .replace(/\s+/g, " ")
    .trim();
}

function formatVitalsForVisit(structuredClinicalData = {}) {
  const data = normalizeVitalRepStructuredClinicalData(structuredClinicalData);
  return [
    ["bloodPressure", normalizeVitalDisplayValue("bloodPressure", data.vitals.bloodPressure)],
    ["heartRate", normalizeVitalDisplayValue("heartRate", data.vitals.heartRate)],
    ["temperature", normalizeVitalDisplayValue("temperature", data.vitals.temperature)],
    ["oxygenSaturation", normalizeVitalDisplayValue("oxygenSaturation", data.vitals.oxygenSaturation)],
    ["respiratoryRate", normalizeVitalDisplayValue("respiratoryRate", data.vitals.respiratoryRate)],
    ["weight", normalizeVitalDisplayValue("weight", data.vitals.weight)],
  ].filter(([, value]) => hasDisplayText(value));
}

function vitalsSourceLabel(source, t) {
  const normalized = String(source || "").trim().toLowerCase();
  if (normalized === VITALREP_VITALS_SOURCE.PATIENT_REPORTED) {
    return t("settings.subflows.patientEhr.vitalsSource.patientReported", "Patient reported");
  }
  if (normalized === VITALREP_VITALS_SOURCE.CLINICIAN_CAPTURED) {
    return t("settings.subflows.patientEhr.vitalsSource.clinicianCaptured", "Clinician captured");
  }
  if (normalized === "home_device" || normalized === "home device" || normalized === "device") {
    return t("settings.subflows.patientEhr.vitalsSource.homeDevice", "Home device");
  }
  if (
    normalized === "uploaded_document" ||
    normalized === "uploaded document" ||
    normalized === "document_import" ||
    normalized === "document"
  ) {
    return t("settings.subflows.patientEhr.vitalsSource.uploadedDocument", "Uploaded document");
  }
  if (normalized === VITALREP_VITALS_SOURCE.NOT_CAPTURED || normalized === "not captured") {
    return t("settings.subflows.patientEhr.vitalsSource.notCaptured", "Not captured");
  }
  return t("settings.subflows.patientEhr.vitalsSource.unspecified", "Source not specified");
}

function buildPatientReadableNoteSummary({vitalRep,t}) {
 const approved=vitalRep?.authorityReportId&&vitalRep?.finalizedBy;
 const unavailable=t('clinical.notePending');
 const data=normalizeVitalRepStructuredClinicalData(approved?vitalRep.structuredClinicalData:{});
 const soap=approved?vitalRep.soapDraft:null;
 return {sourceType:approved?'vitalrep':'pending',sourceLabel:approved?t('clinical.noteApproved'):unavailable,structuredClinicalData:data,hasStructuredClinicalData:!!approved,
 reason:approved?(soap?.subjective||data.reasonForVisit||''):unavailable,
 found:approved?(soap?.assessment||data.assessment||''):'',
 objective:approved?(soap?.objective||data.clinicalObservations||''):'',
 treatment:approved?(soap?.plan||data.treatmentNextSteps||''):'',followUp:approved?data.followUpAdvice:''};
}

function buildLabEntries(snapshot, appointmentId) {
  const sources = [
    ...(Array.isArray(snapshot?.labResults) ? snapshot.labResults : []),
    ...(Array.isArray(snapshot?.testResults) ? snapshot.testResults : []),
    ...(Array.isArray(snapshot?.attachments) ? snapshot.attachments : []),
  ];

  return sources
    .map((item, index) => {
      const title = pickFirstText(item?.name, item?.title, item?.fileName);
      if (!title) return null;
      return {
        id: `${appointmentId || "appointment"}-lab-${index}`,
        title,
        subtitle: pickFirstText(item?.source, item?.author, item?.by),
      };
    })
    .filter(Boolean);
}

function buildPatientVisitSections(visit, t) {
  return [
    {
      key: "reason",
      label: t("settings.subflows.patientEhr.subjectiveReasonForVisit", "Subjective / Reason for visit"),
      value: noteSectionValue(visit.patientSummary.reason, t),
      keepWhenUnavailable: true,
    },
    {
      key: "objective",
      label: t("settings.subflows.patientEhr.objectiveWhatWasFound", "Objective / What was found"),
      value: noteSectionValue(visit.patientSummary.objective, t),
      keepWhenUnavailable: true,
    },
    {
      key: "assessment",
      label: t("settings.subflows.patientEhr.assessmentDiagnosis", "Assessment / Diagnosis"),
      value: noteSectionValue(visit.patientSummary.found, t),
      keepWhenUnavailable: true,
    },
    {
      key: "testsLabs",
      label: t("settings.subflows.patientEhr.testsLabs", "Tests & labs"),
      value: visit.patientSummary.testsLabs,
    },
    {
      key: "treatment",
      label: t("settings.subflows.patientEhr.planTreatmentNextSteps", "Plan / Treatment & next steps"),
      value: noteSectionValue(visit.patientSummary.treatment, t),
      keepWhenUnavailable: true,
    },
    {
      key: "followUp",
      label: t("settings.subflows.patientEhr.followUp", "Follow-up"),
      value: noteSectionValue(visit.patientSummary.followUp, t),
      keepWhenUnavailable: true,
    },
  ];
}

function buildVisitVitalsItems(visit, labelMap, t) {
  const rows = [
    {
      key: "source",
      label: t("settings.subflows.patientEhr.vitals.source", "Source"),
      fullWidth: true,
      value: visit.vitals.length > 0
        ? vitalsSourceLabel(visit.vitalsSource, t)
        : t("settings.subflows.patientEhr.vitalsSource.notCaptured", "Not captured"),
    },
    ...visit.vitals.map(([key, value]) => ({
      key,
      label: labelMap[key] || key,
      value,
    })),
  ];
  return rows;
}

const EMPTY_RECORD = Object.freeze({
  patientId: null,
  appointments: [],
  visits: [],
  prescriptions: [],
  labs: [],
  referrals: [],
  notes: [],
  latestVisit: null,
  latestVitals: null,
  kycStatus: "",
});

const EHR_DIVIDER_RAIL = "-mx-4";
const EHR_DIVIDER_RAIL_WIDE = "-mx-4 sm:-mx-5";

function SectionSurface({ title, subtitle, children }) {
  return (
    <Card as="section" className="p-4">
      <div className="text-title-medium text-on-surface">{title}</div>
      {subtitle ? <div className="mt-1 text-body-small text-on-surface-var">{subtitle}</div> : null}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

function PatientText({ value, className = "" }) {
  const text = cleanPatientReadableText(value);
  if (!text) return null;

  const blocks = text.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);

  return (
    <div className={`space-y-2 text-left text-body-medium leading-6 text-on-surface ${className}`}>
      {blocks.map((block, blockIndex) => {
        const lines = block.split(/\n/).map((line) => line.trim()).filter(Boolean);
        const bulletItems = lines
          .map((line) => line.match(/^•\s+(.+)$/)?.[1])
          .filter(Boolean);
        const orderedItems = lines
          .map((line) => line.match(/^(\d+)\.\s+(.+)$/))
          .filter(Boolean);

        if (bulletItems.length === lines.length) {
          return (
            <ul key={`ul-${blockIndex}`} className="list-disc space-y-1 pl-5 text-left">
              {bulletItems.map((item, itemIndex) => (
                <li key={`${blockIndex}-${itemIndex}`}>{item}</li>
              ))}
            </ul>
          );
        }

        if (orderedItems.length === lines.length) {
          return (
            <ol key={`ol-${blockIndex}`} className="list-decimal space-y-1 pl-5 text-left">
              {orderedItems.map((match, itemIndex) => (
                <li key={`${blockIndex}-${itemIndex}`}>{match[2]}</li>
              ))}
            </ol>
          );
        }

        return (
          <p key={`p-${blockIndex}`} className="text-left">
            {lines.join(" ")}
          </p>
        );
      })}
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-3">
      <div className="text-body-medium text-on-surface-var">{label}</div>
      <div className="min-w-0 text-left text-body-medium leading-6 text-on-surface">{value}</div>
    </div>
  );
}

function shouldShowPatientSummarySection({ action = null, value, keepWhenUnavailable = false } = {}) {
  const hasAction = Boolean(action);
  const text = typeof value === "string" ? cleanPatientReadableText(value) : value;
  return hasAction || keepWhenUnavailable || (!isUnavailableText(text) && !isRawExportNoise(text));
}

function PatientSummarySection({ label, value, action = null, keepWhenUnavailable = false }) {
  const hasAction = Boolean(action);
  const text = typeof value === "string" ? cleanPatientReadableText(value) : value;
  if (!shouldShowPatientSummarySection({ action, value, keepWhenUnavailable })) {
    return null;
  }

  return (
    <section className="py-3 first:pt-0 last:pb-0">
      <div className="text-label-large text-on-surface-var">{label}</div>
      <div className="mt-1">
        {hasAction ? action : typeof text === "string" ? <PatientText value={text} /> : text}
      </div>
    </section>
  );
}

function PatientSummarySectionGroup({ children, className = "", dividerRailClassName = EHR_DIVIDER_RAIL }) {
  const rows = React.Children.toArray(children).filter((child) => {
    if (!React.isValidElement(child)) return Boolean(child);
    if (child.type !== PatientSummarySection) return true;
    return shouldShowPatientSummarySection(child.props);
  });

  if (rows.length === 0) return null;

  return (
    <div className={className}>
      {rows.map((row, index) => (
        <React.Fragment key={row.key || index}>
          {row}
          {index < rows.length - 1 ? (
            <div className={dividerRailClassName}>
              <Divider inset variant="outlineVariant" className="opacity-[0.35]" />
            </div>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}

function CompactInfoGrid({ items, columns = "sm:grid-cols-3" }) {
  const rows = (items || []).filter((item) => item && hasDisplayText(item.value));
  if (rows.length === 0) return null;

  return (
    <div className={`grid grid-cols-1 overflow-hidden rounded-[10px] bg-[rgba(var(--clr-surface-container),0.46)] ring-1 ring-[rgba(var(--clr-outline),0.08)] ${columns}`}>
      {rows.map((item) => (
        <div
          key={item.key || item.label}
          className="min-w-0 border-b border-[rgba(var(--clr-outline),0.06)] p-3 last:border-b-0 sm:border-b-0 sm:border-r sm:border-[rgba(var(--clr-outline),0.06)] sm:last:border-r-0"
        >
          <div className="text-label-large text-on-surface-var">{item.label}</div>
          <div className="mt-1 min-w-0 text-body-medium leading-6 text-on-surface">
            <PatientText value={item.value} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CompactVitalsGrid({ items }) {
  const rows = (items || []).filter((item) => item && hasDisplayText(item.value));
  if (rows.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-x-5 gap-y-4 lg:grid-cols-3">
      {rows.map((item) => (
        <div
          key={item.key || item.label}
          className={`min-w-0 ${item.fullWidth ? "col-span-2 lg:col-span-1" : ""}`}
        >
          <div className="text-label-large text-on-surface-var">{item.label}</div>
          <div className="mt-1 text-title-small text-on-surface">{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function EhrControlButtonLabel({ label, iconSrc, badge = "" }) {
  return (
    <span className="flex w-full min-w-0 items-center justify-center gap-2 whitespace-nowrap text-center">
      <img
        src={iconSrc}
        alt=""
        aria-hidden="true"
        className="hidden"
      />
      <span
        aria-hidden="true"
        className="h-4 w-4 shrink-0 bg-[rgb(var(--afrimed-primary))]"
        style={{
          mask: `url(${iconSrc}) center / contain no-repeat`,
          WebkitMask: `url(${iconSrc}) center / contain no-repeat`,
        }}
      />
      <span className="min-w-0 truncate">{label}</span>
      {badge ? (
        <span className="shrink-0 rounded-full border border-[rgba(var(--clr-outline),0.3)] bg-[rgba(var(--clr-surface-container-high),0.68)] px-2 py-0.5 text-body-small leading-none text-on-surface-var">
          {badge}
        </span>
      ) : null}
    </span>
  );
}

function displayNameFromProfile(row = null, fallback = "") {
  const fullName = pickFirstText(row?.full_name, row?.display_name);
  if (fullName) return resolvePatientFacingDoctorName(fullName);
  const parts = [pickFirstText(row?.first_name), pickFirstText(row?.last_name)].filter(Boolean);
  return parts.length > 0 ? resolvePatientFacingDoctorName(parts.join(" ")) : fallback;
}

function InlineAction({ label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left text-body-medium leading-6 ${
        disabled ? "text-on-surface-var opacity-60" : "text-primary"
      }`}
    >
      {label}
    </button>
  );
}

export default function PatientEHR() {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const auth = useAuth() || {};
  const user = auth.user || null;
  const authLoading = Boolean(auth.loading);
  const state = location.state || null;

  const [unlocked, setUnlocked] = React.useState(false);

  const [loadingData, setLoadingData] = React.useState(false);
  const [enrichingData, setEnrichingData] = React.useState(false);
  const [dataError, setDataError] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("visits");
  const [record, setRecord] = React.useState(EMPTY_RECORD);
  const [infoDialog, setInfoDialog] = React.useState(null);
  const [uploadingDocument, setUploadingDocument] = React.useState(false);
  const [documentActionMessage, setDocumentActionMessage] = React.useState("");
  const fileInputRef = React.useRef(null);

  const goToPrescription = React.useCallback(
    (prescriptionId, appointmentId = null) => {
      const cleanPrescriptionId = normalizeId(prescriptionId);
      if (!cleanPrescriptionId) return;
      setUnlocked(false);
      nav(`/appointments/prescriptions/${cleanPrescriptionId}`, {
        state: {
          from: "patient-ehr",
          appointmentId: normalizeId(appointmentId) || null,
          prescriptionId: cleanPrescriptionId,
          returnTo: location.pathname,
          returnState: state,
        },
      });
    },
    [location.pathname, nav, state],
  );

  const goToVisit = React.useCallback(
    (appointmentId) => {
      const cleanAppointmentId = normalizeId(appointmentId);
      if (!cleanAppointmentId) return;
      setUnlocked(false);
      nav(`/appointments/past/${cleanAppointmentId}`);
    },
    [nav],
  );

  const goToReferral = React.useCallback(
    (referralId) => {
      const cleanReferralId = normalizeId(referralId);
      if (!cleanReferralId) return;
      setUnlocked(false);
      nav(`/patient/referrals/${cleanReferralId}`, {
        state: {
          from: "patient-ehr",
          referralId: cleanReferralId,
          returnTo: location.pathname,
          returnState: state,
        },
      });
    },
    [location.pathname, nav, state],
  );

  const openImportInfo = React.useCallback(() => {
    setInfoDialog("import");
  }, []);

  const triggerUpload = React.useCallback(() => {
    setDocumentActionMessage("");
    fileInputRef.current?.click();
  }, []);

  const handleUploadDocument = React.useCallback(
    async (event) => {
      const file = event.target.files?.[0] || null;
      event.target.value = "";
      if (!file) return;

      const patientId = normalizeId(record.patientId);
      const userId = normalizeId(user?.id);
      if (!patientId || !userId) {
        setDocumentActionMessage(
          t("settings.subflows.patientEhr.uploadMissingPatient", "Unlock myEHR before uploading a document."),
        );
        return;
      }

      const allowedByMime = PATIENT_EHR_UPLOAD_MIMES.has(file.type);
      const allowedByExtension = /\.(pdf|jpe?g|png|webp|heic|heif|txt|rtf|doc|docx)$/i.test(file.name);
      if (!allowedByMime && !allowedByExtension) {
        setDocumentActionMessage(
          t("settings.subflows.patientEhr.uploadUnsupported", "Choose a PDF, image, text, RTF, DOC, or DOCX file."),
        );
        return;
      }

      if (file.size > 25 * 1024 * 1024) {
        setDocumentActionMessage(
          t("settings.subflows.patientEhr.uploadTooLarge", "Choose a file smaller than 25 MB."),
        );
        return;
      }

      setUploadingDocument(true);
      setDocumentActionMessage("");
      try {
        const cleanName = safeFilename(file.name);
        const ext = fileExtension(cleanName);
        const uploadMime = resolveUploadMime(file, ext);
        const objectId =
          typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const storagePath = `patients/${userId}/external-records/${objectId}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from(PATIENT_EHR_DOCUMENT_BUCKET)
          .upload(storagePath, file, {
            upsert: false,
            cacheControl: "3600",
            contentType: uploadMime,
          });
        if (uploadError) throw uploadError;

        const { error: insertError } = await supabase.from("patient_ehr_documents").insert({
          patient_id: patientId,
          user_id: userId,
          storage_bucket: PATIENT_EHR_DOCUMENT_BUCKET,
          storage_path: storagePath,
          file_name: cleanName,
          file_type: uploadMime,
          file_size: file.size,
          source: PATIENT_EHR_UPLOAD_SOURCE,
          uploaded_at: new Date().toISOString(),
        });
        if (insertError) {
          await supabase.storage.from(PATIENT_EHR_DOCUMENT_BUCKET).remove([storagePath]);
          throw insertError;
        }

        setDocumentActionMessage(
          t("settings.subflows.patientEhr.uploadSuccess", "Document uploaded to your external myEHR records."),
        );
      } catch (error) {
        setDocumentActionMessage(
          error?.message ||
            t("settings.subflows.patientEhr.uploadFailed", "We could not upload that document right now."),
        );
      } finally {
        setUploadingDocument(false);
      }
    },
    [record.patientId, t, user?.id],
  );

  const downloadMyEhrExport = React.useCallback(() => {
    const payload = buildMyEhrExport(record);
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `afrimed-my-ehr-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [record]);

  React.useEffect(() => {
    return () => {
      setUnlocked(false);
    };
  }, []);

  const handleBack = React.useCallback(() => {
    const returnTo = String(state?.returnTo || "").trim();
    if (returnTo) {
      nav(returnTo, state?.returnState ? { state: state.returnState } : undefined);
      return;
    }
    nav("/settings/patient/p");
  }, [nav, state]);

  React.useEffect(() => {
    let alive = true;

    async function loadRecord() {
      if (authLoading || !user?.id || !unlocked) return;

      setLoadingData(true);
      setEnrichingData(false);
      setDataError("");
      try {
        const patientUserId = normalizeId(user.id);
        const { data: patientRow, error: patientError } = await supabase
          .from("patients")
          .select("id,user_id")
          .eq("user_id", patientUserId)
          .maybeSingle();
        if (patientError) throw patientError;

        if (!patientRow?.id) {
          if (!alive) return;
          setRecord(EMPTY_RECORD);
          return;
        }

        const [appointmentResult, prescriptionResult, referralResult, idUploadsResult, latestVitalsResult] = await Promise.all([
          supabase
            .from("appointments")
            .select("*")
            .eq("patient_id", patientRow.id)
            .order("appointment_date", { ascending: false, nullsFirst: false })
            .order("updated_at", { ascending: false, nullsFirst: false })
            .order("created_at", { ascending: false }),
          supabase
            .from("prescriptions")
            .select("id,appointment_id,issued_at,created_at,status,meta,external_rx_id,notes,diagnosis,patient_user_id")
            .eq("patient_user_id", patientUserId)
            // An issued prescription is a patient-keyed clinical document. It
            // must not inherit the visit feed's pagination or video-admission
            // filter: those are only presentation rules for appointment visits.
            .eq("status", "issued")
            .order("issued_at", { ascending: false, nullsFirst: false })
            .order("created_at", { ascending: false }),
          supabase
            .from("referrals")
            .select("*")
            .eq("patient_user_id", patientUserId)
            .order("created_at", { ascending: false }),
          supabase
            .from("id_uploads")
            .select("status,created_at")
            .eq("user_id", patientUserId)
            .order("created_at", { ascending: false })
            .limit(1),
          fetchLatestPatientVitalSigns(patientUserId)
            .then((data) => ({ data, error: null }))
            .catch((error) => ({ data: null, error })),
        ]);

        const { data: appointmentRows, error: appointmentError } = appointmentResult;
        if (appointmentError) throw appointmentError;
        const { data: prescriptionRows, error: prescriptionError } = prescriptionResult;
        if (prescriptionError) throw prescriptionError;
        const { data: referralRows, error: referralError } = referralResult;
        if (referralError) throw referralError;
        if (idUploadsResult?.error) throw idUploadsResult.error;
        if (latestVitalsResult?.error) throw latestVitalsResult.error;

        const sortedAppointments = [...(appointmentRows || [])].sort((a, b) => {
          return new Date(getAppointmentTimestamp(b)).getTime() - new Date(getAppointmentTimestamp(a)).getTime();
        });
        const appointmentIds = sortedAppointments.map((row) => normalizeId(row?.id)).filter(Boolean);

        const { data: videoSessionRows, error: videoSessionError } =
          appointmentIds.length > 0
            ? await supabase
                .from("appointment_video_sessions")
                .select("appointment_id,patient_joined_at")
                .in("appointment_id", appointmentIds)
            : { data: [], error: null };
        // Appointment-derived EHR data is safe to show only when participation
        // evidence is readable. Treat an unavailable evidence source as no
        // encounter rather than leaking an unverified clinical record.
        const resolvedVideoSessionRows = videoSessionError ? [] : videoSessionRows || [];

        const patientJoinedAtByAppointmentId = new Map(
          resolvedVideoSessionRows.map((row) => [
            normalizeId(row?.appointment_id),
            String(row?.patient_joined_at || "").trim() || null,
          ]),
        );
        // myEHR's visit feed is a delivered-care record, not an appointment
        // ledger. Fail closed only for the visit-derived projections below.
        const encounterAppointments = sortedAppointments.filter((appointment) =>
          hasPatientConsultationParticipation({
            consultation_type: appointment?.consultation_type,
            patient_joined_at: patientJoinedAtByAppointmentId.get(normalizeId(appointment?.id)),
          }),
        );
        const encounterAppointmentIds = encounterAppointments
          .map((row) => normalizeId(row?.id))
          .filter(Boolean);

        const { data: consultationRows, error: consultationError } =
          encounterAppointmentIds.length > 0
            ? await supabase.from("consultations").select("*").in("appointment_id", encounterAppointmentIds)
            : { data: [], error: null };
        if (consultationError) throw consultationError;

        const consultationByAppointmentId = new Map(
          (consultationRows || []).map((row) => [normalizeId(row?.appointment_id), row]),
        );
        const consultationIds = (consultationRows || []).map((row) => normalizeId(row?.id)).filter(Boolean);
        const noteAuthorityBatches = [];
        for (let index = 0; index < encounterAppointmentIds.length; index += 200) {
          noteAuthorityBatches.push(
            loadClinicalNoteAuthorities(encounterAppointmentIds.slice(index, index + 200)),
          );
        }
        const noteAuthorities = (await Promise.all(noteAuthorityBatches)).flat();
        const reportRows = noteAuthorities.map(a=>a.final_report).filter(Boolean);

        // `prescriptions.patient_user_id` is the canonical Patient key (legacy
        // patient-record IDs were normalised to auth users in the 20260318
        // migration). Keep every issued row returned for this authenticated
        // Patient, irrespective of clinician, appointment age, or whether the
        // appointment is shown in the participation-gated visit feed.
        const issuedPrescriptions = prescriptionRows || [];
        const prescriptionsByAppointmentId = new Map();
        for (const row of issuedPrescriptions) {
          const key = normalizeId(row?.appointment_id);
          if (!key || prescriptionsByAppointmentId.has(key)) continue;
          prescriptionsByAppointmentId.set(key, row);
        }

        const vitalRepByConsultationId = new Map();
        for (const row of reportRows || []) {
          const key = normalizeId(row?.consultation_id);
          if (!key || vitalRepByConsultationId.has(key)) continue;
          vitalRepByConsultationId.set(key, toVitalRepRecord(row));
        }

        const referralDoctorIds = Array.from(
          new Set((referralRows || []).map((row) => normalizeId(row?.from_doctor_user_id)).filter(Boolean)),
        );
        const { data: referralDoctorRows } =
          referralDoctorIds.length > 0
            ? await supabase
                .from("doctor_profiles")
                .select("user_id,full_name,first_name,last_name")
                .in("user_id", referralDoctorIds)
            : { data: [] };
        const referralDoctorNameById = new Map(
          (referralDoctorRows || []).map((row) => [
            normalizeId(row?.user_id),
            displayNameFromProfile(row, t("settings.subflows.patientEhr.clinician", "Clinician")),
          ]),
        );

        function buildRecord(snapshotByAppointmentId = new Map()) {
          const visits = encounterAppointments.map((appointment) => {
            const appointmentId = normalizeId(appointment?.id);
            const snapshot = snapshotByAppointmentId.get(appointmentId) || null;
            const consultation = consultationByAppointmentId.get(appointmentId) || null;
            const vitalRep = vitalRepByConsultationId.get(normalizeId(consultation?.id)) || null;
            const prescription = prescriptionsByAppointmentId.get(appointmentId) || null;

            const prescriptionTitle = prescription
              ? medicineTitleFromPrescription(
                  prescription,
                  t("settings.subflows.patientEhr.prescriptionTitle", "Prescription"),
                )
              : "";
            const noteSummary = buildPatientReadableNoteSummary({
              vitalRep,
              snapshot,
              prescription,
              appointment,
              t,
            });
            const treatmentSummary = noteSummary.treatment;
            const followUpSummary = noteSummary.followUp;
            const notePreview =
              [
                firstAvailableSentence(noteSummary.reason),
                firstAvailableSentence(noteSummary.found),
                firstAvailableSentence(noteSummary.treatment),
              ]
                .filter(hasDisplayText)
                .join(" ")
                .trim() || t("settings.subflows.patientEhr.notAvailable", "Not available");
            const vitals = formatVitalsForVisit(noteSummary.structuredClinicalData);
            const vitalsSource =
              noteSummary.structuredClinicalData?.vitals?.source || VITALREP_VITALS_SOURCE.NOT_CAPTURED;
            const testsLabsRequested = pickFirstText(
              noteSummary.structuredClinicalData?.testsLabsRequested,

            );
            const clinicalObservations = pickFirstText(
              noteSummary.structuredClinicalData?.clinicalObservations,
              noteSummary.objective,
            );
            const objectiveSummary = pickFirstAvailableText(
              noteSummary.objective,
            );

            return {
              id: appointmentId,
              appointment,
              consultation,
              prescription,
              vitalRep,
              snapshot,
              dateLabel:
                formatDateTime(getAppointmentTimestamp(appointment)) ||
                t("settings.subflows.patientEhr.notAvailable", "Not available"),
              serviceLabel:
                displayVisitTitle(
                  pickFirstText(
                    snapshot?.serviceLabel,
                    snapshot?.serviceName,
                    appointment?.booked_service_name,
                    appointment?.consultation_type,
                  ),
                  t("settings.subflows.patientEhr.visitLabel", "Visit"),
                ),
              diagnosis: noteSummary.found || t("settings.subflows.patientEhr.noDiagnosis", "Not available"),
              notesPreview: notePreview,
              statusLabel: normalizeRecordStatus(
                consultation?.status || appointment?.status,
                t("settings.subflows.patientEhr.notAvailable", "Not available"),
              ),
              patientSummary: {
                reason: cleanPatientReadableText(noteSummary.reason),
                found: cleanPatientReadableText(noteSummary.found),
                treatment: cleanPatientReadableText(treatmentSummary),
                prescription:
                  cleanPatientReadableText(noteSummary.structuredClinicalData?.medicationSummary) ||
                  prescriptionTitle ||
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
                followUp: cleanPatientReadableText(followUpSummary),
                observations:
                  cleanPatientReadableText(clinicalObservations) ||
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
                objective:
                  cleanPatientReadableText(objectiveSummary) ||
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
                testsLabs:
                  cleanPatientReadableText(testsLabsRequested) ||
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
              },
              vitals,
              vitalsSource,
              testsLabsRequestedRaw: testsLabsRequested,
              hasStructuredClinicalData: noteSummary.hasStructuredClinicalData,
              noteSourceLabel: noteSummary.sourceLabel,
            };
          });

          const prescriptionItems = issuedPrescriptions.map((row) => ({
            id: normalizeId(row?.id),
            appointmentId: normalizeId(row?.appointment_id),
            rxNo: prescriptionIdentifier(row),
            title: medicineTitleFromPrescription(
              row,
              t("settings.subflows.patientEhr.prescriptionTitle", "Prescription"),
            ),
            issuedLabel:
              formatDateOnly(row?.issued_at || row?.created_at) ||
              t("settings.subflows.patientEhr.notAvailable", "Not available"),
            statusLabel: normalizeRecordStatus(
              row?.status,
              t("settings.subflows.patientEhr.notAvailable", "Not available"),
            ),
          }));

          const referralItems = (referralRows || [])
            .map((row) => {
              const id = normalizeId(row?.id);
              if (!id) return null;
              const specialty = pickFirstText(
                row?.to_specialty_label,
                row?.specialty_label,
                row?.specialty_key,
                row?.referral_type,
              );
              return {
                id,
                appointmentId: normalizeId(row?.appointment_id),
                title:
                  specialty ||
                  t("settings.subflows.patientEhr.referralRecord", "Referral record"),
                dateLabel:
                  formatDateOnly(row?.created_at || row?.updated_at) ||
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
                statusLabel: normalizeRecordStatus(
                  row?.status,
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
                ),
                urgencyLabel: normalizeRecordStatus(
                  row?.urgency,
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
                ),
                issuedBy:
                  referralDoctorNameById.get(normalizeId(row?.from_doctor_user_id)) ||
                  t("settings.subflows.patientEhr.clinician", "Clinician"),
                request:
                  firstSentence(pickFirstText(row?.specific_request, row?.reason, row?.clinical_history)) ||
                  t("settings.subflows.patientEhr.notAvailable", "Not available"),
                documentAccess: t(
                  "settings.subflows.patientEhr.referralSecureLink",
                  "Secure referral link / PDF",
                ),
              };
            })
            .filter(Boolean);

          const labs = visits.flatMap((visit) =>
            [
              ...buildLabEntries(visit.snapshot, visit.id),
              ...(hasDisplayText(visit.testsLabsRequestedRaw)
                ? [
                    {
                      id: `${visit.id}-vitalrep-tests-labs`,
                      title: t("clinical.noteTests"),
                      detail: visit.testsLabsRequestedRaw,
                      subtitle: t("settings.subflows.patientEhr.vitalRepSoapNote", "VitalRep™ SOAP note"),
                    },
                  ]
                : []),
            ].map((entry) => ({
              ...entry,
              appointmentId: visit.id,
              dateLabel: visit.dateLabel,
            })),
          );

          const notes = visits
            .map((visit) => {
              const body = visit.vitalRep?.authorityReportId ? visit.vitalRep.finalNoteText : t('clinical.notePending');
              return {
                id: `note-${visit.id}`,
                appointmentId: visit.id,
                provenance: visit.vitalRep?.authorityReportId ? `${visit.vitalRep.authorityReportId} · v${visit.vitalRep.noteVersion} · ${visit.vitalRep.finalizedAt}` : '',
                title: visit.noteSourceLabel || t("settings.subflows.patientEhr.visitNote", "Visit note"),
                body,
                summary: visit.patientSummary,
                dateLabel: visit.dateLabel,
              };
            })
            .filter(Boolean);

          return {
            patientId: patientRow.id,
            appointments: encounterAppointments,
            visits,
            prescriptions: prescriptionItems,
            labs,
            referrals: referralItems,
            notes,
            latestVisit: visits[0] || null,
            latestVitals: latestVitalsResult?.data || null,
            kycStatus: String(idUploadsResult?.data?.[0]?.status || "").trim(),
          };
        }

        if (!alive) return;
        setRecord(buildRecord());
        setLoadingData(false);

        if (encounterAppointmentIds.length > 0) {
          setEnrichingData(true);
          Promise.all(
            encounterAppointmentIds.map(async (appointmentId) => {
              try {
                const snapshot = await loadPatientAppointmentSnapshot(appointmentId);
                return [appointmentId, snapshot];
              } catch {
                return [appointmentId, null];
              }
            }),
          )
            .then((snapshotEntries) => {
              if (!alive) return;
              setRecord(buildRecord(new Map(snapshotEntries)));
            })
            .finally(() => {
              if (alive) setEnrichingData(false);
            });
        }
      } catch (error) {
        if (!alive) return;
        setDataError(
          error?.message ||
            t(
              "settings.subflows.patientEhr.loadError",
              "We could not load your health record right now.",
            ),
        );
        setRecord(EMPTY_RECORD);
      } finally {
        if (alive) setLoadingData(false);
      }
    }

    loadRecord();
    return () => {
      alive = false;
    };
  }, [authLoading, t, unlocked, user?.id]);

  const latestVisit = record.latestVisit;
  const latestCanonicalVitals = React.useMemo(
    () => formatCanonicalVitals(record.latestVitals),
    [record.latestVitals],
  );
  const vitalLabelMap = React.useMemo(
    () => ({
      bloodPressure: t("settings.subflows.patientEhr.vitals.bloodPressure", "BP"),
      heartRate: t("settings.subflows.patientEhr.vitals.heartRate", "Heart rate"),
      temperature: t("settings.subflows.patientEhr.vitals.temperature", "Temperature"),
      oxygenSaturation: t("settings.subflows.patientEhr.vitals.oxygenSaturation", "Oxygen"),
      respiratoryRate: t("settings.subflows.patientEhr.vitals.respiratoryRate", "Resp. rate"),
      weight: t("settings.subflows.patientEhr.vitals.weight", "Weight"),
      height: t("settings.subflows.patientEhr.vitals.height", "Height"),
    }),
    [t],
  );
  const latestVisitSummaryItems = React.useMemo(
    () => [
      {
        key: "diagnosis",
        label: t("settings.subflows.patientEhr.latestVisitDiagnosis", "Diagnosis"),
        value:
          firstAvailableSentence(latestVisit?.patientSummary?.found) ||
          firstAvailableSentence(latestVisit?.diagnosis) ||
          t("settings.subflows.patientEhr.notAvailable", "Not available"),
      },
      {
        key: "prescription",
        label: t("settings.subflows.patientEhr.latestVisitPrescription", "Prescription"),
        value: latestVisit?.prescription
          ? medicineTitleFromPrescription(
              latestVisit.prescription,
              t("settings.subflows.patientEhr.prescriptionTitle", "Prescription"),
            )
          : t("settings.subflows.patientEhr.notAvailable", "Not available"),
      },
      {
        key: "notes",
        label: t("settings.subflows.patientEhr.latestVisitNotes", "Notes preview"),
        value:
          firstAvailableSentence(latestVisit?.notesPreview) ||
          t("settings.subflows.patientEhr.notAvailable", "Not available"),
      },
    ],
    [latestVisit, t],
  );
  const latestVitalsItems = React.useMemo(
    () => {
      if (latestCanonicalVitals.length === 0) return [];
      return [
        {
          key: "source",
          label: t("settings.subflows.patientEhr.vitals.source", "Source"),
          fullWidth: true,
          value: vitalsSourceLabel(record.latestVitals?.source, t),
        },
        ...latestCanonicalVitals.map(([key, value]) => ({
          key,
          label: vitalLabelMap[key] || key,
          value,
        })),
      ];
    },
    [latestCanonicalVitals, record.latestVitals?.source, t, vitalLabelMap],
  );
  const latestPrescriptionId = normalizeId(latestVisit?.prescription?.id || state?.prescriptionId);
  const latestAppointmentId = normalizeId(latestVisit?.id || state?.appointmentId);
  const actualHasPassword = hasPasswordConfigured(user);
  const ehrPasswordPreview = String(searchParams.get("ehrPassword") || "").trim().toLowerCase();
  const allowPasswordPreview =
    import.meta.env.DEV || import.meta.env.MODE === "staging";
  const hasPassword =
    allowPasswordPreview && ehrPasswordPreview === "missing"
      ? false
      : allowPasswordPreview && ehrPasswordPreview === "ready"
        ? true
        : actualHasPassword;

  return (
    <div data-hide-tabbar="true" className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-[calc(128px+env(safe-area-inset-bottom))] md:pb-12">
        <TopBarCenter
          title={t("settings.subflows.patientEhr.title", "My Health Record")}
          subtitle={t(
            "settings.subflows.patientEhr.subtitle",
            "Your visits, prescriptions, referrals, tests and clinician notes — secured in one place.",
          )}
          onBack={handleBack}
        />

        <div className="mt-5">
          <FolaOnboardingCard role="patient" />
        </div>

        {!authLoading && user?.id && hasPassword && !unlocked ? (
          <PatientPasswordLifecyclePanel
            context="ehr"
            hasPasswordOverride={hasPassword}
            className="mt-6"
            onUnlocked={() => setUnlocked(true)}
          />
        ) : null}

        {!authLoading && user?.id && !hasPassword && !unlocked ? (
          <PatientPasswordLifecyclePanel
            context="ehr"
            hasPasswordOverride={hasPassword}
            className="mt-6"
            onCreated={() => setUnlocked(true)}
            onUnlocked={() => setUnlocked(true)}
          />
        ) : null}

        {!authLoading && unlocked ? (
          <>
            <ClinicalActionPanel patientView />
            <section className="mt-5">
              <div className="mt-4 space-y-4">
                <SectionSurface
                  title={t("settings.subflows.patientEhr.latestVisitSummary", "Latest visit summary")}
                  subtitle={
                    latestVisit
                      ? latestVisit.dateLabel
                      : t(
                          "settings.subflows.patientEhr.noVisitSummary",
                          "Your latest recorded visit details will appear here.",
                        )
                  }
                >
                  <CompactInfoGrid items={latestVisitSummaryItems} />
                </SectionSurface>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Button
                    type="button"
                    disabled={!latestPrescriptionId}
                    onClick={() => goToPrescription(latestPrescriptionId, latestAppointmentId)}
                  >
                    {t("settings.subflows.patientEhr.viewPrescription", "View prescription")}
                  </Button>
                  <Button
                    type="button"
                    variant="tonal"
                    disabled={!latestAppointmentId}
                    onClick={() => goToVisit(latestAppointmentId)}
                  >
                    {t("settings.subflows.patientEhr.viewVisit", "View visit")}
                  </Button>
                </div>

                <SectionSurface
                  title={t("settings.subflows.patientEhr.latestVitals", "Latest vitals")}
                  subtitle={
                    record.latestVitals?.captured_at || record.latestVitals?.recorded_at
                      ? formatDateTime(record.latestVitals.captured_at || record.latestVitals.recorded_at)
                      : t(
                          "settings.subflows.patientEhr.noLatestVitals",
                          "No canonical vitals have been recorded yet.",
                        )
                  }
                >
                  {latestCanonicalVitals.length > 0 ? (
                    <CompactVitalsGrid items={latestVitalsItems} />
                  ) : (
                    <div className="text-body-medium text-on-surface-var">
                      {t(
                        "settings.subflows.patientEhr.noLatestVitals",
                        "No canonical vitals have been recorded yet.",
                      )}
                    </div>
                  )}
                </SectionSurface>

                <section>
                  <div className="text-title-medium text-on-surface">
                    {t("settings.subflows.patientEhr.recordControlsTitle", "My Health Record controls")}
                  </div>
                  <div className="mt-1 text-body-small text-on-surface-var">
                    {t(
                      "settings.subflows.patientEhr.recordControlsSubtitle",
                      "Import and export controls for patient-owned health records.",
                    )}
                  </div>
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <Button type="button" variant="tonal" onClick={openImportInfo} className="min-w-0 px-3">
                        <EhrControlButtonLabel
                          iconSrc="/icons/import.svg"
                          label={t(
                            "settings.subflows.patientEhr.importExternalRecord",
                            "Import external health records",
                          )}
                        />
                      </Button>
                      <Button
                        type="button"
                        variant="tonal"
                        onClick={triggerUpload}
                        loading={uploadingDocument}
                        className="min-w-0 px-3"
                      >
                        <EhrControlButtonLabel
                          iconSrc="/icons/upload.svg"
                          label={t(
                            "settings.subflows.patientEhr.uploadExternalRecord",
                            "Upload PDF/image/document",
                          )}
                        />
                      </Button>
                      <Button
                        type="button"
                        variant="tonal"
                        onClick={downloadMyEhrExport}
                        className="min-w-0 px-3"
                      >
                        <EhrControlButtonLabel
                          iconSrc="/icons/list-items/download.svg"
                          label={t(
                            "settings.subflows.patientEhr.downloadExport",
                            "Download myEHR export",
                          )}
                          badge={t("settings.subflows.patientEhr.beta", "Beta")}
                        />
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.txt,.rtf,.doc,.docx,application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,text/plain,text/rtf,application/rtf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleUploadDocument}
                    />
                    {documentActionMessage ? (
                      <div className="text-body-small leading-5 text-on-surface-var">
                        {documentActionMessage}
                      </div>
                    ) : null}
                    <div className="text-body-small leading-5 text-on-surface-var">
                      {t(
                        "settings.subflows.patientEhr.recordControlsHint",
                        "Patient-owned records can be imported, uploaded, and exported as AfriMed™ expands interoperability support. Structured exchange will prioritise modern health data standards, while PDFs and documents remain available for human-readable records.",
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </section>

            <div className="chips-viewport mt-5 no-scrollbar">
              <div className="chips-row">
                <AfriPill
                  value="visits"
                  label={t("settings.subflows.patientEhr.tabs.visits", "Visits")}
                  isActive={activeTab === "visits"}
                  onChange={setActiveTab}
                  variant="appointments"
                />
                <AfriPill
                  value="prescriptions"
                  label={t("settings.subflows.patientEhr.tabs.prescriptions", "Prescriptions")}
                  isActive={activeTab === "prescriptions"}
                  onChange={setActiveTab}
                  variant="appointments"
                />
                <AfriPill
                  value="labs"
                  label={t("settings.subflows.patientEhr.tabs.labs", "Tests & Labs")}
                  isActive={activeTab === "labs"}
                  onChange={setActiveTab}
                  variant="appointments"
                />
                <AfriPill
                  value="referrals"
                  label={t("settings.subflows.patientEhr.tabs.referrals", "Referrals")}
                  isActive={activeTab === "referrals"}
                  onChange={setActiveTab}
                  variant="appointments"
                />
                <AfriPill
                  value="notes"
                  label={t("settings.subflows.patientEhr.tabs.notes", "Notes")}
                  isActive={activeTab === "notes"}
                  onChange={setActiveTab}
                  variant="appointments"
                />
              </div>
            </div>

            {loadingData ? (
              <Card className="mt-5 p-4 text-body-medium text-on-surface-var">
                {t("settings.subflows.patientEhr.loading", "Loading your health record...")}
              </Card>
            ) : null}

            {dataError ? (
              <Card className="mt-5 p-4 text-body-medium text-[rgb(var(--afrimed-error))]">
                {dataError}
              </Card>
            ) : null}

            {!loadingData && !dataError && activeTab === "visits" ? (
              <section className="mt-5">
                <div className="text-title-medium text-on-surface">
                  {t("settings.subflows.patientEhr.visitHistoryTitle", "Visit history")}
                </div>
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t(
                    "settings.subflows.patientEhr.visitHistorySubtitle",
                    "Recorded visits with appointment lineage only.",
                  )}
                </div>
                {enrichingData ? (
                  <div className="mt-2 text-body-small text-on-surface-var">
                    {t("settings.subflows.patientEhr.enrichingVisits", "Adding visit details...")}
                  </div>
                ) : null}
                {record.visits.length === 0 ? (
                  <div className="mt-4 text-body-medium text-on-surface-var">
                    {t("settings.subflows.patientEhr.noVisits", "No visit history is available yet.")}
                  </div>
                ) : (
                  <div className="mt-4 space-y-4">
                    {record.visits.map((visit) => (
                      <Card
                        as="article"
                        key={visit.id}
                        data-appointment-id={visit.id}
                        className="p-4 sm:p-5"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="text-title-medium text-on-surface">{visit.serviceLabel}</div>
                            <ClinicalNoteProvenance note={visit.vitalRep} />
                            <div className="mt-1 text-body-small text-on-surface-var">{visit.dateLabel}</div>
                          </div>
                          <div className="shrink-0 whitespace-nowrap text-right text-label-large text-on-surface-var">
                            {visit.statusLabel}
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="mb-2 text-label-large text-on-surface-var">
                            {t("settings.subflows.patientEhr.vitals", "Vitals")}
                          </div>
                          <CompactVitalsGrid items={buildVisitVitalsItems(visit, vitalLabelMap, t)} />
                        </div>
                        <PatientSummarySectionGroup className="mt-4" dividerRailClassName={EHR_DIVIDER_RAIL_WIDE}>
                          {buildPatientVisitSections(visit, t).map((section) => (
                            <PatientSummarySection
                              key={section.key}
                              label={section.label}
                              value={section.value}
                              action={section.action}
                              keepWhenUnavailable={section.keepWhenUnavailable}
                            />
                          ))}
                        </PatientSummarySectionGroup>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            {!loadingData && !dataError && activeTab === "prescriptions" ? (
              <section className="mt-5">
                <div className="text-title-medium text-on-surface">
                  {t("settings.subflows.patientEhr.prescriptionHistoryTitle", "Past prescriptions")}
                </div>
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t(
                    "settings.subflows.patientEhr.prescriptionHistorySubtitle",
                    "Medication records and prescription documents tied to recorded appointments.",
                  )}
                </div>
                {record.prescriptions.length === 0 ? (
                  <div className="mt-4 text-body-medium text-on-surface-var">
                    {t(
                      "settings.subflows.patientEhr.noPrescriptions",
                      "No prescriptions are available in your health record yet.",
                    )}
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {record.prescriptions.map((prescription) => (
                      <Card
                        as="article"
                        key={prescription.id}
                        className="p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="text-title-medium text-on-surface">
                            <InlineAction
                              label={[prescription.rxNo, prescription.title].filter(Boolean).join(" · ")}
                              onClick={() => goToPrescription(prescription.id, prescription.appointmentId)}
                            />
                          </div>
                          <div className="text-body-medium text-on-surface-var">{prescription.issuedLabel}</div>
                        </div>
                        <div className="mt-3 space-y-3">
                          <SummaryLine
                            label={t("settings.subflows.patientEhr.prescriptionStatus", "Prescription status")}
                            value={prescription.statusLabel}
                          />
                          <SummaryLine
                            label={t("settings.subflows.patientEhr.appointmentReferenceShort", "Appointment")}
                            value={
                              prescription.id ? (
                                <InlineAction
                                  label={
                                    prescription.appointmentId ||
                                    t("settings.subflows.patientEhr.viewPrescription", "View prescription")
                                  }
                                  onClick={() => goToPrescription(prescription.id, prescription.appointmentId)}
                                />
                              ) : (
                                prescription.appointmentId ||
                                t("settings.subflows.patientEhr.notAvailable", "Not available")
                              )
                            }
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            {!loadingData && !dataError && activeTab === "labs" ? (
              <section className="mt-5">
                <div className="text-title-medium text-on-surface">
                  {t("settings.subflows.patientEhr.labsTitle", "Tests & Labs")}
                </div>
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t(
                    "settings.subflows.patientEhr.labsSubtitle",
                    "Requested tests, lab results, imaging, or uploaded reports when available.",
                  )}
                </div>
                {record.labs.length === 0 ? (
                  <div className="mt-4 text-body-medium text-on-surface-var">
                    {t(
                      "settings.subflows.patientEhr.noLabs",
                      "No tests or lab results are available yet.",
                    )}
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {record.labs.map((entry) => (
                      <Card
                        as="article"
                        key={entry.id}
                        className="p-4 sm:p-5"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                          <div className="text-title-medium text-on-surface">{entry.title}</div>
                          <div className="shrink-0 text-body-medium text-on-surface-var">
                            {entry.dateLabel || t("settings.subflows.patientEhr.notAvailable", "Not available")}
                          </div>
                        </div>
                        <PatientSummarySectionGroup className="mt-3" dividerRailClassName={EHR_DIVIDER_RAIL_WIDE}>
                          {entry.detail ? (
                            <PatientSummarySection
                              label={t("settings.subflows.patientEhr.testsLabsDetails", "Details")}
                              value={entry.detail}
                            />
                          ) : null}
                          <PatientSummarySection
                            label={t("settings.subflows.patientEhr.labSource", "Source")}
                            value={entry.subtitle || t("settings.subflows.patientEhr.notAvailable", "Not available")}
                            keepWhenUnavailable
                          />
                          <PatientSummarySection
                            label={t("settings.subflows.patientEhr.appointmentReferenceShort", "Appointment")}
                            value={entry.appointmentId || t("settings.subflows.patientEhr.notAvailable", "Not available")}
                            keepWhenUnavailable
                          />
                        </PatientSummarySectionGroup>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            {!loadingData && !dataError && activeTab === "referrals" ? (
              <section className="mt-5">
                <div className="text-title-medium text-on-surface">
                  {t("settings.subflows.patientEhr.referralsTitle", "Referrals")}
                </div>
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t(
                    "settings.subflows.patientEhr.referralsSubtitle",
                    "Doctor-issued referral records and referral PDFs or secure links where available.",
                  )}
                </div>
                {record.referrals.length === 0 ? (
                  <div className="mt-4 text-body-medium text-on-surface-var">
                    {t(
                      "settings.subflows.patientEhr.noReferrals",
                      "No referral records are available yet.",
                    )}
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {record.referrals.map((referral) => (
                      <Card
                        as="article"
                        key={referral.id}
                        className="p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-title-medium text-on-surface">
                              <InlineAction
                                label={referral.title}
                                onClick={() => goToReferral(referral.id)}
                              />
                            </div>
                            <div className="mt-1 text-body-small text-on-surface-var">{referral.dateLabel}</div>
                          </div>
                          <div className="text-label-large text-on-surface-var">{referral.statusLabel}</div>
                        </div>
                        <div className="mt-3 space-y-3">
                          <SummaryLine
                            label={t("settings.subflows.patientEhr.issuedBy", "Issued by")}
                            value={referral.issuedBy}
                          />
                          <SummaryLine
                            label={t("settings.subflows.patientEhr.referralRequest", "Referral request")}
                            value={referral.request}
                          />
                          <SummaryLine
                            label={t("settings.subflows.patientEhr.urgency", "Urgency")}
                            value={referral.urgencyLabel}
                          />
                          <SummaryLine
                            label={t("settings.subflows.patientEhr.referralDocument", "Referral document")}
                            value={
                              <InlineAction
                                label={referral.documentAccess}
                                onClick={() => goToReferral(referral.id)}
                              />
                            }
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            {!loadingData && !dataError && activeTab === "notes" ? (
              <section className="mt-5">
                <div className="text-title-medium text-on-surface">
                  {t("settings.subflows.patientEhr.notesTitle", "Visit notes")}
                </div>
                <div className="mt-1 text-body-small text-on-surface-var">
                  {t(
                    "settings.subflows.patientEhr.notesSubtitle",
                    "VitalRep™ SOAP notes and clinician notes when available.",
                  )}
                </div>
                {record.notes.length === 0 ? (
                  <div className="mt-4 text-body-medium text-on-surface-var">
                    {t(
                      "settings.subflows.patientEhr.noNotesRecorded",
                      "No visit notes are available yet.",
                    )}
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {record.notes.map((entry) => (
                      <Card
                        as="article"
                        key={entry.id}
                        data-appointment-id={entry.appointmentId}
                        className="p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="text-title-medium text-on-surface">{entry.title}</div>
                          <div className="text-body-medium text-on-surface-var">{entry.dateLabel}</div>
                        </div>
                        {entry.provenance&&<p className="text-body-small text-on-surface-var" data-testid="note-version">{entry.provenance}</p>}
                        <PatientSummarySectionGroup className="mt-3">
                          <PatientSummarySection
                            label={t("settings.subflows.patientEhr.subjectiveReasonForVisit", "Subjective / Reason for visit")}
                            value={noteSectionValue(entry.summary?.reason, t)}
                            keepWhenUnavailable
                          />
                          <PatientSummarySection
                            label={t("settings.subflows.patientEhr.objectiveWhatWasFound", "Objective / What was found")}
                            value={noteSectionValue(entry.summary?.objective, t)}
                            keepWhenUnavailable
                          />
                          <PatientSummarySection
                            label={t("settings.subflows.patientEhr.assessmentDiagnosis", "Assessment / Diagnosis")}
                            value={noteSectionValue(entry.summary?.found, t)}
                            keepWhenUnavailable
                          />
                          <PatientSummarySection
                            label={t("settings.subflows.patientEhr.planTreatmentNextSteps", "Plan / Treatment & next steps")}
                            value={noteSectionValue(entry.summary?.treatment, t)}
                            keepWhenUnavailable
                          />
                          <PatientSummarySection
                            label={t("settings.subflows.patientEhr.followUp", "Follow-up")}
                            value={noteSectionValue(entry.summary?.followUp, t)}
                            keepWhenUnavailable
                          />
                        </PatientSummarySectionGroup>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            ) : null}
          </>
        ) : null}
      </div>
      <Dialog
        open={infoDialog === "import"}
        onClose={() => setInfoDialog(null)}
        title={t("settings.subflows.patientEhr.importExternalRecord", "Import external health records")}
        footer={
          <Button type="button" onClick={() => setInfoDialog(null)}>
            {t("settings.subflows.patientEhr.gotIt", "Got it")}
          </Button>
        }
      >
        <div className="text-body-medium leading-6 text-on-surface-var">
          {t(
            "settings.subflows.patientEhr.importInfo",
            "Structured import from clinics, hospitals, laboratories, and external health providers is being prepared. For now, you can upload PDF, image, and document copies into your patient-owned external myEHR records.",
          )}
        </div>
      </Dialog>
    </div>
  );
}
